import React from 'react';
import { createPortal } from 'react-dom';

import ConfirmationModal from './ConfirmationModal';
import { useRoutePortalTarget } from './layout/RoutePortalHost';
import { clearCrewDMForUser } from '../services/crewDMs';
import { blockUser } from '../services/users';
import type { ToastType } from '../types';

const clean = (value: unknown) => String(value ?? '').trim();

type Props = {
  dmId: string;
  threadExists?: boolean;
  currentUid: string;
  targetUid: string;
  targetName?: string;
  isDarkMode: boolean;
  addToast: (message: string, type?: ToastType) => void;
  onDeleted?: () => void;
  onBlocked?: () => void;
};

const CrewDMConversationMenu: React.FC<Props> = ({
  dmId,
  threadExists = true,
  currentUid,
  targetUid,
  targetName,
  isDarkMode,
  addToast,
  onDeleted,
  onBlocked,
}) => {
  const portalTarget = useRoutePortalTarget();
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [confirmAction, setConfirmAction] = React.useState<'delete' | 'block' | null>(null);
  const [position, setPosition] = React.useState({ top: 0, left: 0 });

  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  const canAct = Boolean(clean(dmId) && clean(currentUid) && clean(targetUid));

  React.useEffect(() => {
    if (!open) return;

    const close = (event: MouseEvent | TouchEvent) => {
      const node = event.target as Node | null;
      if (node && buttonRef.current?.contains(node)) return;
      if (node && menuRef.current?.contains(node)) return;
      setOpen(false);
    };

    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    window.addEventListener('mousedown', close);
    window.addEventListener('touchstart', close, { passive: true });
    window.addEventListener('keydown', escape);

    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('touchstart', close);
      window.removeEventListener('keydown', escape);
    };
  }, [open]);

  const toggle = () => {
    if (!canAct || busy) return;
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({
        top: Math.round(rect.bottom + 7),
        left: Math.round(rect.right),
      });
    }
    setOpen((current) => !current);
  };

  const confirmDelete = async () => {
    if (busy || !threadExists) return;
    try {
      setBusy(true);
      await clearCrewDMForUser(currentUid, dmId);
      setConfirmAction(null);
      addToast('Chat removed from your Crew messages.', 'success');
      onDeleted?.();
    } catch (error: any) {
      addToast(clean(error?.message) || 'Could not remove this chat.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const confirmBlock = async () => {
    if (busy) return;
    try {
      setBusy(true);
      await blockUser(currentUid, targetUid, 'Blocked from Crew message');
      setConfirmAction(null);
      addToast(`${clean(targetName) || 'Angler'} blocked.`, 'success');
      onBlocked?.();
    } catch (error: any) {
      addToast(clean(error?.message) || 'Could not block this angler.', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!canAct) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        className="grid h-9 w-9 shrink-0 place-items-center rounded-full border text-[17px] font-black transition hover:-translate-y-px active:translate-y-0 active:scale-95"
        style={{
          background: isDarkMode ? '#101A2A' : '#FFFFFF',
          borderColor: isDarkMode ? 'rgba(132,151,190,0.13)' : 'rgba(91,67,49,0.09)',
          color: isDarkMode ? '#DCE4EF' : '#62584F',
          boxShadow: isDarkMode
            ? '0 4px 12px rgba(0,0,0,0.14)'
            : '0 4px 12px rgba(89,65,37,0.06)',
        }}
        aria-label="Conversation options"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Conversation options"
      >
        ⋯
      </button>

      {open && portalTarget
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              className="fixed z-[99999] w-44 overflow-hidden rounded-[13px] border py-1"
              style={{
                top: position.top,
                left: position.left,
                transform: 'translateX(-100%)',
                background: isDarkMode ? '#101827' : '#FFFCF7',
                borderColor: isDarkMode ? 'rgba(132,151,190,0.18)' : 'rgba(74,58,42,0.14)',
                boxShadow: isDarkMode
                  ? '0 14px 30px rgba(0,0,0,.32)'
                  : '0 14px 30px rgba(62,45,28,.14)',
              }}
            >
              <button
                type="button"
                role="menuitem"
                disabled={!threadExists}
                onClick={() => {
                  if (!threadExists) return;
                  setOpen(false);
                  setConfirmAction('delete');
                }}
                className="w-full px-4 py-3 text-left text-[12px] font-black transition hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-35 dark:hover:bg-white/5"
                style={{ color: isDarkMode ? '#E7EDF6' : '#433A32' }}
                title={threadExists ? 'Delete chat' : 'No messages to delete yet'}
              >
                Delete chat
              </button>

              <div
                className="mx-3 h-px"
                style={{ background: isDarkMode ? 'rgba(132,151,190,0.10)' : 'rgba(74,58,42,0.08)' }}
              />

              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  setConfirmAction('block');
                }}
                className="w-full px-4 py-3 text-left text-[12px] font-black transition hover:bg-black/5 dark:hover:bg-white/5"
                style={{ color: isDarkMode ? '#FF9AA5' : '#A63D49' }}
              >
                Block user
              </button>
            </div>,
            portalTarget,
          )
        : null}

      <ConfirmationModal
        isOpen={confirmAction === 'delete'}
        onClose={() => {
          if (!busy) setConfirmAction(null);
        }}
        onConfirm={() => {
          void confirmDelete();
        }}
        title="Delete chat?"
        message="This removes the conversation from your Crew messages only. The other angler keeps their copy, and a new message can make the chat appear again."
        confirmText={busy ? 'Deleting...' : 'Delete chat'}
        cancelText="Cancel"
        isDarkMode={isDarkMode}
      />

      <ConfirmationModal
        isOpen={confirmAction === 'block'}
        onClose={() => {
          if (!busy) setConfirmAction(null);
        }}
        onConfirm={() => {
          void confirmBlock();
        }}
        title={`Block ${clean(targetName) || 'this angler'}?`}
        message="They will be removed from your Crew connection and new private Crew messages will stop until you unblock them."
        confirmText={busy ? 'Blocking...' : 'Block user'}
        cancelText="Cancel"
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default CrewDMConversationMenu;
